import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Heart, RefreshCw, Loader2, Users, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/waitlist`);
      setEntries(res.data);
    } catch (e) {
      setError("Warteliste konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const syncToBrevo = async () => {
    if (!window.confirm(
      "Alle Einträge aus Supabase zu Brevo Liste 3 synchronisieren?\n\n" +
      "Neue Kontakte werden hinzugefügt (Welcome-Automation triggert). " +
      "Bereits vorhandene werden aktualisiert."
    )) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/waitlist/sync-brevo`);
      const { total, synced, failed } = res.data;
      toast.success(`Sync abgeschlossen`, {
        description: `${synced} von ${total} Kontakten zu Brevo übertragen.${
          failed?.length ? ` ${failed.length} fehlgeschlagen.` : ""
        }`,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sync fehlgeschlagen.";
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBFB] font-body text-qh-ink" data-testid="admin-page">
      <Toaster richColors position="top-center" />
      {/* Top nav */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 h-20 sm:h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
            <img
              src="/assets/qhearts-logo.png"
              alt="QHearts"
              className="h-14 sm:h-16 w-auto object-contain select-none"
              draggable="false"
            />
            <span className="ml-1 text-xs uppercase tracking-widest text-qh-slate">
              admin
            </span>
          </Link>
          <Link to="/">
            <Button
              variant="ghost"
              className="rounded-full text-sm"
              data-testid="admin-back-home"
            >
              Zur Webseite
            </Button>
          </Link>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
              Warteliste
            </span>
            <h1 className="mt-3 font-display font-medium text-4xl sm:text-5xl tracking-tight">
              Anmeldungen
            </h1>
            <p className="mt-3 text-qh-slate">
              Menschen, die früh dabei sein möchten. Neueste zuerst.
            </p>
          </div>
          <Button
            onClick={syncToBrevo}
            variant="outline"
            className="rounded-full border-qh-rose text-qh-rose hover:bg-[#FFE4E6]"
            disabled={syncing || loading || entries.length === 0}
            data-testid="admin-sync-brevo"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {syncing ? "Synchronisiere…" : "Zu Brevo sync"}
          </Button>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-full border-qh-line"
            disabled={loading}
            data-testid="admin-refresh"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Aktualisieren
          </Button>
        </div>

        {/* Stats */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-qh-line bg-white p-6" data-testid="admin-stat-total">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#FFE4E6] flex items-center justify-center">
                <Users className="w-4 h-4 text-qh-rose" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-qh-slate">
                  Alle Anmeldungen
                </div>
                <div className="font-display text-3xl font-medium mt-0.5">
                  {entries.length}
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-qh-line bg-white p-6" data-testid="admin-stat-last24">
            <div className="text-xs uppercase tracking-wider text-qh-slate">
              Letzte 24h
            </div>
            <div className="font-display text-3xl font-medium mt-2">
              {
                entries.filter(
                  (e) =>
                    new Date(e.created_at).getTime() >
                    Date.now() - 24 * 60 * 60 * 1000
                ).length
              }
            </div>
          </div>
          <div className="rounded-2xl border border-qh-line bg-white p-6" data-testid="admin-stat-last-signup">
            <div className="text-xs uppercase tracking-wider text-qh-slate">
              Zuletzt eingetragen
            </div>
            <div className="font-display text-lg font-medium mt-2 truncate">
              {entries[0] ? entries[0].email : "—"}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-10 rounded-2xl border border-qh-line bg-white overflow-hidden">
          {(() => {
            if (loading && entries.length === 0) {
              return (
                <div className="py-20 flex items-center justify-center text-qh-slate">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lädt…
                </div>
              );
            }
            if (error) {
              return (
                <div className="py-20 text-center text-qh-slate" data-testid="admin-error">
                  {error}
                </div>
              );
            }
            if (entries.length === 0) {
              return (
                <div className="py-20 text-center text-qh-slate" data-testid="admin-empty">
                  Noch keine Anmeldungen. Teile die Seite mit den Menschen, die dir
                  wichtig sind.
                </div>
              );
            }
            return (
              <Table data-testid="admin-table">
                <TableHeader>
                  <TableRow className="border-qh-line">
                    <TableHead className="text-qh-slate uppercase text-xs tracking-wider">
                      E-Mail
                    </TableHead>
                    <TableHead className="text-qh-slate uppercase text-xs tracking-wider">
                      Name
                    </TableHead>
                    <TableHead className="text-qh-slate uppercase text-xs tracking-wider text-right">
                      Eingetragen
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} className="border-qh-line" data-testid={`admin-row-${e.id}`}>
                      <TableCell className="font-medium">{e.email}</TableCell>
                      <TableCell className="text-qh-slate">
                        {e.name || "—"}
                      </TableCell>
                      <TableCell className="text-qh-slate text-right">
                        {formatDate(e.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </div>
      </main>
    </div>
  );
}
